import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { useCxcFiliales } from "../hooks/useCxcFiliales";
import PdfEstadoModal from "../components/PdfEstadoModal";
import EmailEstadoCuentaModal from "../components/EmailEstadoCuentaModal";
import MultaModal from "../components/MultaModal";
import { TableToolbar, FilterPills, FilterField, FilterInput } from "../../shared/TableToolbar";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";

import invoiceStyles from "../../invoices/styles/invoice.module.css";

// ── Helpers de fecha ─────────────────────────────────────────────────────────

function primerDiaMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function ultimoDiaMes() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
}

// ── Componente principal ─────────────────────────────────────────────────────

export default function ListCxC() {
  const { t } = useTranslation();
  const esCliente = useEsCliente();

  const FILTROS = [
    { value: "debiendo", label: t("cxc.filtroConSaldo"), icon: "fa-exclamation-circle" },
    { value: "aldia",    label: t("cxc.filtroAlDia"),   icon: "fa-check-circle"        },
    { value: "todos",    label: t("cxc.filtroTodos"),   icon: "fa-list"                },
  ];

  const [filtro,      setFiltro]      = useState("debiendo");
  const [search,      setSearch]      = useState("");
  const [refreshKey,  setRefreshKey]  = useState(0);
  const [pdfModal,    setPdfModal]    = useState(null);
  const [page,        setPage]        = useState(1);

  // Date range — default: current month
  const [fechaInicio, setFechaInicio] = useState(primerDiaMes);
  const [fechaFinal,  setFechaFinal]  = useState(ultimoDiaMes);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showMultaModal, setShowMultaModal] = useState(false);

  const PAGE_SIZE = 12;
  const { filas, todasFilas, loading } = useCxcFiliales(filtro, search, refreshKey, fechaInicio, fechaFinal);

  const totalPages = Math.ceil(filas.length / PAGE_SIZE);
  const filasPage  = filas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Derived selection list (only rows visible in filtered set)
  const selectedRows = useMemo(
    () => filas.filter((r) => selectedIds.has(r.filialId)),
    [filas, selectedIds],
  );

  const allPageSelected = filasPage.length > 0 && filasPage.every((r) => selectedIds.has(r.filialId));

  function handleSearch(val) { setSearch(val); setPage(1); }
  function handleFiltro(val) { setFiltro(val); setPage(1); }

  function toggleSelect(filialId) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(filialId) ? next.delete(filialId) : next.add(filialId);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allPageSelected) {
      // Deselect all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filasPage.forEach((r) => next.delete(r.filialId));
        return next;
      });
    } else {
      // Select all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filasPage.forEach((r) => next.add(r.filialId));
        return next;
      });
    }
  }

  function selectAll() {
    setSelectedIds(new Set(filas.map((r) => r.filialId)));
  }
  function clearSelection() { setSelectedIds(new Set()); }

  const totalSaldo = filas.reduce((a, r) => a + r.totalSaldo, 0);
  const totalMonto = filas.reduce((a, r) => a + r.totalMonto, 0);

  // Período activo = distinto del mes actual (por defecto)
  const periodoActivo = fechaInicio !== primerDiaMes() || fechaFinal !== ultimoDiaMes();

  function exportarExcel() {
    const filas_export = todasFilas.map((r) => ({
      Filial:              r.filialLabel,
      Propietario:         r.propietario || "",
      "Total Facturado":   r.totalMonto,
      "Total Abonado":     r.totalAbono,
      "Saldo Acumulado":   r.totalSaldo,
      "Facturado Período": r.facturadoPeriodo ?? r.totalMonto,
      "Abonado Período":   r.abonadoPeriodo   ?? r.totalAbono,
      Estado:              r.totalSaldo <= 0 ? "Al día" : r.totalAbono > 0 ? "Parcial" : "Pendiente",
    }));
    filas_export.push({
      Filial: "TOTAL", Propietario: "",
      "Total Facturado": todasFilas.reduce((a, r) => a + r.totalMonto, 0),
      "Total Abonado":   todasFilas.reduce((a, r) => a + r.totalAbono, 0),
      "Saldo Acumulado": todasFilas.reduce((a, r) => a + r.totalSaldo, 0),
    });
    const ws = XLSX.utils.json_to_sheet(filas_export);
    ws["!cols"] = [{ wch: 12 }, { wch: 36 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CxC");
    const fecha = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    XLSX.writeFile(wb, `CxC_Todas_Filiales_${fecha}.xlsx`);
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Breadcrumb ─────────────────────────────────────────────── */}
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>{t("common.inicio")} /</span></Link>
        <span className={invoiceStyles.active}> {t("cxc.titulo")}</span>
      </span>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2" style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("cxc.gestion")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("cxc.administra")}</p>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {/* Email Estado de Cuenta */}
          <button
            onClick={() => setShowEmailModal(true)}
            disabled={loading || selectedIds.size === 0}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
              background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.35)",
              borderRadius: 8, color: "#60a5fa", fontSize: "0.83rem", fontWeight: 600,
              cursor: loading || selectedIds.size === 0 ? "not-allowed" : "pointer",
              opacity: selectedIds.size === 0 ? 0.5 : 1, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { if (selectedIds.size > 0) e.currentTarget.style.background = "rgba(96,165,250,0.2)"; }}
            onMouseOut={(e) => { e.currentTarget.style.background = "rgba(96,165,250,0.1)"; }}
            title={selectedIds.size === 0 ? "Seleccione filiales primero" : `Enviar a ${selectedIds.size} filial(es)`}
          >
            <i className="fa-solid fa-envelope" />
            Estado de Cuenta
            {selectedIds.size > 0 && (
              <span style={{
                background: "#60a5fa", color: "#fff", borderRadius: "50%",
                width: 18, height: 18, fontSize: "0.7rem", fontWeight: 700,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                {selectedIds.size}
              </span>
            )}
          </button>

          {/* Multa */}
          <button
            onClick={() => setShowMultaModal(true)}
            disabled={loading || selectedIds.size === 0}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
              background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.35)",
              borderRadius: 8, color: "#f87171", fontSize: "0.83rem", fontWeight: 600,
              cursor: loading || selectedIds.size === 0 ? "not-allowed" : "pointer",
              opacity: selectedIds.size === 0 ? 0.5 : 1, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { if (selectedIds.size > 0) e.currentTarget.style.background = "rgba(248,113,113,0.2)"; }}
            onMouseOut={(e) => { e.currentTarget.style.background = "rgba(248,113,113,0.1)"; }}
            title={selectedIds.size === 0 ? "Seleccione filiales primero" : `Multa a ${selectedIds.size} filial(es)`}
          >
            <i className="fa-solid fa-gavel" />
            Multa / Sanción
            {selectedIds.size > 0 && (
              <span style={{
                background: "#f87171", color: "#fff", borderRadius: "50%",
                width: 18, height: 18, fontSize: "0.7rem", fontWeight: 700,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                {selectedIds.size}
              </span>
            )}
          </button>

          {/* Excel */}
          <button
            onClick={exportarExcel}
            disabled={loading || filas.length === 0}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px",
              background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.35)",
              borderRadius: 8, color: "#4ade80", fontSize: "0.83rem", fontWeight: 600,
              cursor: loading || filas.length === 0 ? "not-allowed" : "pointer",
              opacity: loading || filas.length === 0 ? 0.5 : 1, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { if (!loading && filas.length > 0) e.currentTarget.style.background = "rgba(74,222,128,0.2)"; }}
            onMouseOut={(e) => { e.currentTarget.style.background = "rgba(74,222,128,0.1)"; }}
          >
            <i className="fa-solid fa-file-excel" />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* ── Cards resumen ──────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
        <SummaryCard
          label={t("cxc.filialesConSaldo")}
          value={filas.filter((r) => r.totalSaldo > 0).length}
          icon="fa-house-circle-exclamation"
          color="#f97316"
          isCurrency={false}
        />
        <SummaryCard
          label={t("cxc.totalFacturado")}
          value={totalMonto}
          icon="fa-file-invoice-dollar"
          color="#60a5fa"
        />
        <SummaryCard
          label={t("cxc.totalPendiente")}
          value={totalSaldo}
          icon="fa-money-bill-wave"
          color={totalSaldo > 0 ? "#f87171" : "#4ade80"}
        />
      </div>

      {/* ── Tabla principal ────────────────────────────────────────── */}
      <div className={invoiceStyles["table-container"]}>
        {/* Barra de filtros unificada: búsqueda + estado (pills) + período (panel) */}
        <TableToolbar
          search={search}
          onSearch={handleSearch}
          searchPlaceholder={t("cxc.buscarPlaceholder")}
          loading={loading}
          extra={<FilterPills options={FILTROS} value={filtro} onChange={handleFiltro} />}
          activeFilterCount={periodoActivo ? 1 : 0}
          onClearFilters={() => { setFechaInicio(primerDiaMes()); setFechaFinal(ultimoDiaMes()); setPage(1); }}
          filters={
            <>
              <FilterField label={`${t("cxc.periodo", "Período")} — desde`}>
                <FilterInput
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFechaInicio(val);
                    if (val && val.length === 10) {
                      const [y, m] = val.split("-").map(Number);
                      const lastDay = new Date(y, m, 0).getDate();
                      setFechaFinal(`${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`);
                    }
                    setPage(1);
                  }}
                />
              </FilterField>
              <FilterField label={`${t("cxc.periodo", "Período")} — hasta`}>
                <FilterInput
                  type="date"
                  value={fechaFinal}
                  onChange={(e) => { setFechaFinal(e.target.value); setPage(1); }}
                />
              </FilterField>
            </>
          }
        />

        {/* Barra de selección */}
        {selectedIds.size > 0 && (
          <div style={{
            padding: "8px 16px", background: "rgba(249,115,22,0.06)",
            borderBottom: "1px solid rgba(249,115,22,0.15)",
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          }}>
            <span style={{ fontSize: "0.82rem", color: "#f97316", fontWeight: 600 }}>
              <i className="fa-solid fa-check-square" style={{ marginRight: 5 }} />
              {selectedIds.size} filial{selectedIds.size !== 1 ? "es" : ""} seleccionada{selectedIds.size !== 1 ? "s" : ""}
            </span>
            <button onClick={selectAll} style={{
              padding: "3px 10px", fontSize: "0.78rem", borderRadius: 6,
              border: "1px solid rgba(249,115,22,0.3)", background: "transparent",
              color: "#f97316", cursor: "pointer",
            }}>
              Seleccionar todas ({filas.length})
            </button>
            <button onClick={clearSelection} style={{
              padding: "3px 10px", fontSize: "0.78rem", borderRadius: 6,
              border: "1px solid var(--border-card)", background: "transparent",
              color: "var(--text-muted)", cursor: "pointer",
            }}>
              Limpiar selección
            </button>
          </div>
        )}

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                {/* Checkbox all */}
                <th style={{ width: 36, paddingRight: 0 }}>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleSelectAll}
                    style={{ cursor: "pointer", accentColor: "#f97316" }}
                    title="Seleccionar / deseleccionar página"
                  />
                </th>
                <th>{t("common.filial")}</th>
                {!esCliente && <th>{t("cxc.propietario")}</th>}
                <th className="text-end" title="Facturado en el período seleccionado">
                  Facturado <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>período</span>
                </th>
                <th className="text-end" title="Abonado en el período seleccionado">
                  Abonado <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>período</span>
                </th>
                <th className="text-end" title="Saldo acumulado ALL TIME (independiente del período)">{t("cxc.saldoAcumulado")}</th>
                <th className="text-end">{t("common.estado")}</th>
                <th className="text-center">{t("common.acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "32px", color: "var(--text-secondary)" }}>
                    <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8, color: "#f97316" }} />
                    {t("cxc.cargando")}
                  </td>
                </tr>
              ) : filasPage.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "32px", color: "var(--text-muted)" }}>
                    <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }} />
                    {t("cxc.sinFiliales")}
                  </td>
                </tr>
              ) : (
                filasPage.map((row) => (
                  <CxCRow
                    key={row.filialId}
                    row={row}
                    selected={selectedIds.has(row.filialId)}
                    onToggleSelect={() => toggleSelect(row.filialId)}
                    onPdf={(filialId, idFilial) => setPdfModal({ id: filialId, idFilial })}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                {t("common.cargandoDatos")}
              </span>
            ) : (
              <>
                <span>{filas.length}</span> {t("cxc.filiales")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {/* ── Modal PDF Estado de Cuenta ────────────────────────────── */}
      {pdfModal !== null && (
        <PdfEstadoModal
          onClose={() => setPdfModal(null)}
          filialPreselected={pdfModal.id ? pdfModal : null}
        />
      )}

      {/* ── Modal Email Estado de Cuenta ─────────────────────────── */}
      {showEmailModal && (
        <EmailEstadoCuentaModal
          selectedIds={selectedRows}
          fechaInicio={fechaInicio}
          fechaFinal={fechaFinal}
          onClose={() => setShowEmailModal(false)}
          onDone={() => { setShowEmailModal(false); setRefreshKey((k) => k + 1); }}
        />
      )}

      {/* ── Modal Multa / Sanción ─────────────────────────────────── */}
      {showMultaModal && (
        <MultaModal
          selectedIds={selectedRows}
          onClose={() => setShowMultaModal(false)}
          onDone={() => { setShowMultaModal(false); setSelectedIds(new Set()); setRefreshKey((k) => k + 1); }}
        />
      )}
    </>
  );
}

// ── Fila de la tabla ──────────────────────────────────────────────────────────

function CxCRow({ row, selected, onToggleSelect, onPdf }) {
  const esCliente = useEsCliente();
  const { t } = useTranslation();
  const saldo    = row.totalSaldo;
  const alDia    = saldo <= 0;
  const parcial  = !alDia && row.totalAbono > 0;
  const periodo_fac = row.facturadoPeriodo ?? row.totalMonto;
  const periodo_abo = row.abonadoPeriodo   ?? row.totalAbono;

  return (
    <tr style={{ background: selected ? "rgba(249,115,22,0.06)" : undefined }}>
      {/* Checkbox */}
      <td style={{ width: 36, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          style={{ cursor: "pointer", accentColor: "#f97316" }}
        />
      </td>

      <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{row.filialLabel}</td>
      {!esCliente && (
        <td>{row.propietario || <span style={{ color: "var(--text-dim)" }}>—</span>}</td>
      )}

      {/* Facturado período */}
      <td className="text-end" style={{ color: "var(--text-secondary)" }}>
        ₡{formatCurrency(periodo_fac)}
      </td>

      {/* Abonado período */}
      <td className="text-end" style={{ color: periodo_abo > 0 ? "#4ade80" : "var(--text-secondary)" }}>
        ₡{formatCurrency(periodo_abo)}
      </td>

      {/* Saldo acumulado (ALL TIME) */}
      <td className="text-end" style={{ fontWeight: 600, color: alDia ? "#4ade80" : "#f87171" }}>
        ₡{formatCurrency(Math.abs(saldo))}
        {saldo < 0 && (
          <span style={{ fontSize: "0.72rem", marginLeft: 4 }}>{t("cxc.aFavor")}</span>
        )}
      </td>

      {/* Estado */}
      <td className="text-end">
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "3px 10px", borderRadius: 20, fontSize: "0.74rem", fontWeight: 600,
          background: alDia ? "rgba(74,222,128,0.1)" : parcial ? "rgba(251,191,36,0.1)" : "rgba(248,113,113,0.1)",
          color: alDia ? "#4ade80" : parcial ? "#fbbf24" : "#f87171",
          border: `1px solid ${alDia ? "rgba(74,222,128,0.25)" : parcial ? "rgba(251,191,36,0.25)" : "rgba(248,113,113,0.25)"}`,
        }}>
          <i className={`fa-solid ${alDia ? "fa-check" : parcial ? "fa-clock" : "fa-exclamation"}`}
             style={{ fontSize: "0.65rem" }} />
          {alDia ? t("cxc.alDia") : parcial ? t("common.parcial") : t("common.pendiente")}
        </span>
      </td>

      {/* Acciones */}
      <td className="d-flex text-center justify-content-center">
        <button
          title={t("cxc.estadoPDF")}
          onClick={() => onPdf(row.filialId, row.filialLabel)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "4px 12px", background: "transparent",
            border: "1px solid rgba(249,115,22,0.3)", borderRadius: 20,
            color: "#f97316", fontSize: "0.76rem", fontWeight: 600,
            cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
          }}
          onMouseOver={(e) => { e.currentTarget.style.background = "rgba(249,115,22,0.15)"; e.currentTarget.style.borderColor = "#f97316"; }}
          onMouseOut={(e)  => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(249,115,22,0.3)"; }}
        >
          <i className="fa-solid fa-file-pdf" style={{ fontSize: "0.7rem" }} />
          {t("cxc.estadoPDF")}
        </button>
      </td>
    </tr>
  );
}

// ── Summary card ──────────────────────────────────────────────────────────────

function SummaryCard({ label, value, icon, color, isCurrency = true }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "1.1rem" }} />
      </div>
      <div>
        <p style={{
          margin: 0, color: "var(--text-muted)", fontSize: "0.72rem",
          fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px",
        }}>{label}</p>
        <p style={{ margin: "2px 0 0", color: "var(--text-primary)", fontSize: "1.1rem", fontWeight: 700 }}>
          {isCurrency ? `₡${formatCurrency(value)}` : value}
        </p>
      </div>
    </div>
  );
}
